import { Box, getVariant, normalizeMarkdownListIndentation } from '@apia/theme';
import { memo, useMemo } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Templater } from '../../../templater/Templater';
import { Remarkable } from 'remarkable';

const MARKDOWN_BLOCK_LINES = 25;
const MIN_RESIZABLE_TABLE_COLUMN_WIDTH = 72;
const RESIZABLE_TABLE_HANDLE_SELECTOR =
  '[data-markdown-table-resize-handle="true"]';
const markdownRenderer = new Remarkable({ linkTarget: '_blank' });

type TMarkdownBlock = {
  id: string;
  str: string;
};

type TProtectedMarkdownRange = {
  end: number;
  start: number;
};

const LIST_ITEM_PATTERN = /^\s{0,3}(?:[-+*]|\d+[.)])\s+/;
const TABLE_SEPARATOR_PATTERN =
  /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/;
const FENCE_PATTERN = /^\s{0,3}(`{3,}|~{3,})/;

function countLeadingWhitespace(line: string) {
  const match = line.match(/^\s*/);
  return match ? match[0].length : 0;
}

function isListItem(line: string) {
  return LIST_ITEM_PATTERN.test(line);
}

function isTableRow(line: string) {
  const trimmed = line.trim();
  return trimmed.includes('|') && trimmed.length > 0;
}

function isTableSeparator(line: string) {
  return TABLE_SEPARATOR_PATTERN.test(line);
}

function isFenceClose(line: string, marker: string) {
  const indentation = countLeadingWhitespace(line);
  if (indentation > 3) {
    return false;
  }

  const cleanLine = line.slice(indentation).trimEnd();
  const markerChar = marker[0];

  return (
    cleanLine.length >= marker.length &&
    cleanLine.split('').every((char) => char === markerChar)
  );
}

function isIndentedContinuation(line: string, listIndent: number) {
  const trimmed = line.trim();
  if (!trimmed) {
    return true;
  }

  const indent = countLeadingWhitespace(line);
  return indent > listIndent;
}

function findFenceEnd(lines: string[], start: number) {
  const marker = lines[start].match(FENCE_PATTERN)?.[1];
  if (!marker) {
    return start + 1;
  }

  for (let index = start + 1; index < lines.length; index += 1) {
    if (isFenceClose(lines[index], marker)) {
      return index + 1;
    }
  }

  return lines.length;
}

function findTableEnd(lines: string[], start: number) {
  let end = start + 2;
  while (end < lines.length && isTableRow(lines[end])) {
    end += 1;
  }
  return end;
}

function findListEnd(lines: string[], start: number) {
  const baseIndent = countLeadingWhitespace(lines[start]);
  let end = start + 1;

  while (end < lines.length) {
    const line = lines[end];
    if (isListItem(line) || isIndentedContinuation(line, baseIndent)) {
      end += 1;
      continue;
    }
    break;
  }

  return end;
}

function collectProtectedMarkdownRanges(lines: string[]) {
  const ranges: TProtectedMarkdownRange[] = [];

  for (let index = 0; index < lines.length; ) {
    const line = lines[index];
    if (FENCE_PATTERN.test(line)) {
      const end = findFenceEnd(lines, index);
      ranges.push({ end, start: index });
      index = end;
      continue;
    }

    if (
      index + 1 < lines.length &&
      isTableRow(line) &&
      isTableSeparator(lines[index + 1])
    ) {
      const end = findTableEnd(lines, index);
      ranges.push({ end, start: index });
      index = end;
      continue;
    }

    if (isListItem(line)) {
      const end = findListEnd(lines, index);
      ranges.push({ end, start: index });
      index = end;
      continue;
    }

    index += 1;
  }

  return ranges;
}

function extendPastProtectedMarkdownRange(
  end: number,
  ranges: TProtectedMarkdownRange[],
) {
  let nextEnd = end;
  let changed = true;

  while (changed) {
    changed = false;
    for (const range of ranges) {
      if (range.start < nextEnd && nextEnd < range.end) {
        nextEnd = range.end;
        changed = true;
      }
    }
  }

  return nextEnd;
}

function splitMarkdownBlocks(str: string): TMarkdownBlock[] {
  const lines = str.split('\n');
  const blocks: TMarkdownBlock[] = [];
  const protectedRanges = collectProtectedMarkdownRanges(lines);

  for (let start = 0; start < lines.length; ) {
    const end = extendPastProtectedMarkdownRange(
      Math.min(start + MARKDOWN_BLOCK_LINES, lines.length),
      protectedRanges,
    );
    blocks.push({
      id: `markdown-block-${start}`,
      str: lines.slice(start, end).join('\n'),
    });
    start = end;
  }

  return blocks;
}

function addResizableTableMarkup(html: string) {
  return html
    .replace(/<table([^>]*)>/g, (match, attributes: string) => {
      if (/\bclass=/.test(attributes)) {
        return match.replace(
          /\bclass=(["'])(.*?)\1/,
          'class=$1$2 markdownResizableTable$1',
        );
      }

      return `<table${attributes} class="markdownResizableTable">`;
    })
    .replace(
      /<\/th>/g,
      '<span aria-hidden="true" class="markdownTableResizeHandle" data-markdown-table-resize-handle="true"></span></th>',
    );
}

function getDirectColGroup(table: HTMLTableElement) {
  return Array.from(table.children).find(
    (child) => child.tagName.toLowerCase() === 'colgroup',
  ) as HTMLElement | undefined;
}

function getTableColumnCount(table: HTMLTableElement) {
  return Array.from(table.rows).reduce(
    (count, row) => Math.max(count, row.cells.length),
    0,
  );
}

function ensureTableColGroup(table: HTMLTableElement) {
  const columnCount = getTableColumnCount(table);
  if (columnCount === 0) {
    return undefined;
  }

  const colGroup = getDirectColGroup(table) ?? document.createElement('colgroup');
  if (!colGroup.parentElement) {
    table.insertBefore(colGroup, table.firstChild);
  }

  while (colGroup.children.length < columnCount) {
    colGroup.appendChild(document.createElement('col'));
  }
  while (colGroup.children.length > columnCount) {
    colGroup.lastElementChild?.remove();
  }

  const sizingRow =
    Array.from(table.rows).find((row) => row.cells.length === columnCount) ??
    table.rows[0];

  Array.from(colGroup.children).forEach((col, index) => {
    const element = col as HTMLElement;
    if (element.style.width) {
      return;
    }

    const cell = sizingRow?.cells.item(index);
    const width = cell?.getBoundingClientRect().width;
    element.style.width = `${Math.max(
      MIN_RESIZABLE_TABLE_COLUMN_WIDTH,
      Math.round(width || MIN_RESIZABLE_TABLE_COLUMN_WIDTH),
    )}px`;
  });

  table.style.tableLayout = 'fixed';
  if (!table.style.width) {
    table.style.width = `${Math.max(
      table.getBoundingClientRect().width,
      MIN_RESIZABLE_TABLE_COLUMN_WIDTH * columnCount,
    )}px`;
  }

  return colGroup;
}

function getResizeHandleContext(target: EventTarget | null) {
  const element = target instanceof Element ? target : undefined;
  const handle = element?.closest(RESIZABLE_TABLE_HANDLE_SELECTOR);
  const cell = handle?.closest('th');
  const table = cell?.closest('table');

  if (
    !(cell instanceof HTMLTableCellElement) ||
    !(table instanceof HTMLTableElement)
  ) {
    return undefined;
  }

  const colGroup = ensureTableColGroup(table);
  const col = colGroup?.children.item(cell.cellIndex) as HTMLElement | null;
  if (!col) {
    return undefined;
  }

  return { cell, col, table };
}

function handleTableResizePointerDown(ev: ReactPointerEvent<HTMLElement>) {
  const context = getResizeHandleContext(ev.target);
  if (!context) {
    return;
  }

  ev.preventDefault();
  ev.stopPropagation();

  const { cell, col, table } = context;
  const startX = ev.clientX;
  const startWidth = cell.getBoundingClientRect().width;
  const startTableWidth = table.getBoundingClientRect().width;
  const originalCursor = document.body.style.cursor;
  const originalUserSelect = document.body.style.userSelect;

  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  const handlePointerMove = (event: PointerEvent) => {
    const nextWidth = Math.max(
      MIN_RESIZABLE_TABLE_COLUMN_WIDTH,
      Math.round(startWidth + event.clientX - startX),
    );
    const widthDelta = nextWidth - startWidth;

    col.style.width = `${nextWidth}px`;
    table.style.width = `${Math.max(
      startTableWidth + widthDelta,
      MIN_RESIZABLE_TABLE_COLUMN_WIDTH,
    )}px`;
  };

  const handlePointerUp = () => {
    window.removeEventListener('pointermove', handlePointerMove);
    document.body.style.cursor = originalCursor;
    document.body.style.userSelect = originalUserSelect;
  };

  window.addEventListener('pointermove', handlePointerMove);
  window.addEventListener('pointerup', handlePointerUp, { once: true });
}

const MarkdownBlockRenderer = memo(
  ({ block }: { block: TMarkdownBlock }) => {
    return (
      <>
        {Templater.instance.parseString(
          addResizableTableMarkup(
            markdownRenderer.render(normalizeMarkdownListIndentation(block.str)),
          ),
        )}
      </>
    );
  },
  (previous, next) =>
    previous.block.id === next.block.id && previous.block.str === next.block.str,
);

const MDRenderer = ({ str }: { str: string }) => {
  const blocks = useMemo(() => splitMarkdownBlocks(str), [str]);

  return (
    <Box
      {...getVariant('layout.common.markdown')}
      onPointerDown={handleTableResizePointerDown}
    >
      {blocks.map((block) => (
        <MarkdownBlockRenderer block={block} key={block.id} />
      ))}
    </Box>
  );
};
export default memo(MDRenderer);
